import { useMutation } from '@apollo/client';
import type { CustomRoleUserUpdateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  CustomRoleUserUpdateDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateCustomRolesTRPCInput } from './useUpdateCustomRolesTRPC';
import { useUpdateCustomRolesTRPC } from './useUpdateCustomRolesTRPC';

/**
 * Flag-gated wrapper. Consumers ALWAYS import this hook (never the
 * `*TRPC` sibling directly). When the `trpc` feature flag is on, the
 * call is routed to the tRPC mutation; otherwise it falls back to the
 * legacy Apollo `customRoleUserUpdate` GraphQL action so deployments
 * without the flag continue to function.
 */
export const useUpdateCustomRoles = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // Apollo mutation — legacy fallback. Evict cached custom_role_user
  // entries and refetch the by-id-with-roles query so the user detail
  // modal reflects the change without a page reload.
  const [updateCustomRolesGraphQL, graphqlState] = useMutation(
    CustomRoleUserUpdateDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_role_user');
        evictField(cache, 'auth_user');
        evictField(cache, 'auth_user_by_pk');
      },
      refetchQueries: [namedOperations.Query.GetAuthUserByIdWithRoles],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateCustomRolesTRPC();

  const updateCustomRoles = async (
    variables: UpdateCustomRolesTRPCInput
  ): Promise<CustomRoleUserUpdateMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateCustomRoles(variables);
    }

    const result = await updateCustomRolesGraphQL({
      variables: {
        input: {
          UserId: variables.userId,
          CustomRoleIds: variables.customRoleIds,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to update custom roles');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateCustomRoles,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateCustomRoles,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
